import { ReactNode, useEffect } from "react";
import { extractBrandSlug } from "@/lib/branding";
import { useGeneralFormStore } from "@/stores/generalFormStore";

// WHY: SPA must not trust the wire — re-validate brandSlug before interpolating into
// a redirect URL, mirroring BrandingProvider's hasRedirectFields guard. The DB CHECK
// constraint prevents arbitrary values today, but a future DB-bypass path that wrote
// brandSlug="evil.com?" would interpolate to https://evil.com?.gcheckin.com/... (open redirect).
const SLUG_PATTERN = /^[a-z0-9-]{3,40}$/;

/**
 * Wraps the form-route subtree (/f/:formId and its nested guest/vehicle/pet routes).
 * When the form-app loads on the canonical or legacy default domain AND the form's
 * Owner has a Business-tier brand slug (populated by useGeneralFormStore.initializeForm
 * from PublicFormDataApiDto.brandSlug), redirects to the branded subdomain preserving
 * path + search.
 *
 * On branded subdomains this component is a passthrough — BrandingProvider handles
 * slug-keyed branding via the public branding endpoint.
 *
 * # WHY subscribe-not-fetch: the form-app already fetches PublicFormDataApiDto via
 * useGeneralFormStore.initializeForm. Duplicating the fetch here would double cold-start
 * latency on every form load. Subscribing to the brandSlug slice keeps the fetch path
 * single-source.
 */
export function FormDataBrandRedirect({ children }: { children: ReactNode }) {
  const brandSlug = useGeneralFormStore((state) => state.brandSlug);

  useEffect(() => {
    if (!brandSlug) return;
    if (!SLUG_PATTERN.test(brandSlug)) return;
    const currentSlug = extractBrandSlug(window.location.hostname);
    if (currentSlug) return;
    const branded = `https://${brandSlug}.gcheckin.com${window.location.pathname}${window.location.search}`;
    window.location.replace(branded);
  }, [brandSlug]);

  return <>{children}</>;
}
